/**
 * Forced second-factor enrolment gate.
 *
 * Reached when the backend flags an account `must_enroll_mfa`. The
 * ProtectedRoute guard pins the user here (after any password-change) until
 * enrolment completes. Full-screen, outside MainLayout — the same framing as
 * the forced password-change page.
 *
 * Ceremony: setup (QR) → confirm a live code → reveal recovery codes ONCE →
 * "I've saved these" refreshes /auth/me (which now reports must_enroll_mfa
 * false), updates the store, and routes to the role dashboard.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { authUserFromProfile, type MfaSetup } from '../../services/auth.service';
import { useAuthStore } from '../../stores/authStore';
import { dashboardPathForRole } from '../../lib/roleRouting';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import RecoveryCodesPanel from '../../components/security/RecoveryCodesPanel';
import { colors, gradients, shadows } from '../../styles/theme';

type Phase = 'loading' | 'scan' | 'recovery';

export default function MfaEnroll(): JSX.Element {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [phase, setPhase] = useState<Phase>('loading');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finish = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // Force a fresh /auth/me so the now-cleared must_enroll_mfa flag
      // lands in the store and the route guard lets the user through.
      const profile = await authService.getCurrentUser(true);
      setUser(authUserFromProfile(profile));
      navigate(dashboardPathForRole(profile.role), { replace: true });
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not finish setup. Please try again.'));
      setBusy(false);
    }
  }, [navigate, setUser]);

  const beginSetup = useCallback(async () => {
    setError(null);
    try {
      const data = await authService.setupMfa();
      setSetup(data);
      setPhase('scan');
    } catch (e: unknown) {
      // Already enrolled (e.g. a stale flag) — the flag should be gone on
      // the next /auth/me; send the user on rather than dead-ending.
      if (getApiErrorCode(e) === 'mfa_already_enrolled') {
        await finish();
        return;
      }
      setError(getApiErrorMessage(e, 'Could not start two-factor setup.'));
      setPhase('scan');
    }
  }, [finish]);

  useEffect(() => { beginSetup(); }, [beginSetup]);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await authService.confirmMfa(code.trim());
      setRecoveryCodes(res.recovery_codes);
      setPhase('recovery');
    } catch (e: unknown) {
      // The pending enrolment lapsed (server-side 10-min TTL). Restart the
      // ceremony in place with a fresh secret/QR — the route guard pins every
      // other path back to this page, so an error banner alone is a dead end.
      if (getApiErrorCode(e) === 'mfa_setup_expired') {
        setCode('');
        await beginSetup();
        setError('That setup session expired — a new QR was generated. Scan it and enter the current code.');
      } else {
        setError(getApiErrorMessage(e, 'That code did not match — try the current one.'));
      }
    } finally {
      setBusy(false);
    }
  }, [code, beginSetup]);

  const signOut = useCallback(() => {
    // Escape hatch for "wrong account / can't finish now": the guard pins
    // every authenticated route here, so leaving requires ending the session.
    // logout() revokes both tokens server-side and clears the store.
    authService.logout();
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: gradients.navy }}>
      <div className="w-full max-w-md">
        <div className="rounded-2xl p-9" style={{ background: colors.surface, boxShadow: shadows.card }}>
          <h2 className="text-2xl font-bold mb-1" style={{ color: colors.ink }}>
            {phase === 'recovery' ? 'Save your recovery codes' : 'Set up two-factor authentication'}
          </h2>
          <p className="text-sm mb-7" style={{ color: colors.muted }}>
            {phase === 'recovery'
              ? 'Store these somewhere safe before you continue.'
              : 'Your account requires an authenticator app before you can continue. Scan the code below and enter the 6-digit code it shows.'}
          </p>

          {error && (
            <div className="mb-5 p-3 rounded-lg text-sm" role="alert"
                 style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
              {error}
            </div>
          )}

          {phase === 'loading' && (
            <p className="text-sm" style={{ color: colors.muted }}>Preparing setup…</p>
          )}

          {phase === 'scan' && setup && (
            <div>
              <div className="flex flex-col items-center gap-3 mb-4">
                <img
                  src={setup.qr_data_uri}
                  alt="Two-factor authentication QR code"
                  width={176}
                  height={176}
                  className="rounded-lg"
                  style={{ border: `1px solid ${colors.border}`, background: '#fff' }}
                />
                <code className="block text-xs break-all p-2 rounded-md font-mono w-full text-center"
                      style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
                  {extractSecret(setup.provisioning_uri)}
                </code>
              </div>

              <label htmlFor="enroll-code" className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                     style={{ color: colors.inkSoft }}>
                Authentication code
              </label>
              <input
                id="enroll-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="123456"
                disabled={busy}
                className="w-full px-4 py-2.5 text-sm rounded-xl outline-none font-mono tracking-widest"
                style={{ background: colors.canvas, border: `1.5px solid ${colors.border}`, color: colors.ink }}
              />

              <button
                type="button"
                onClick={() => { confirm(); }}
                disabled={busy || code.trim().length < 6}
                className="w-full py-3 rounded-xl font-semibold text-sm mt-4"
                style={{
                  background: busy || code.trim().length < 6 ? colors.disabledBg : gradients.gold,
                  color: busy || code.trim().length < 6 ? colors.disabledText : colors.navy,
                  cursor: busy || code.trim().length < 6 ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Verifying…' : 'Verify & turn on'}
              </button>
            </div>
          )}

          {phase === 'recovery' && (
            <div>
              <RecoveryCodesPanel codes={recoveryCodes} />
              <button
                type="button"
                onClick={() => { finish(); }}
                disabled={busy}
                className="w-full py-3 rounded-xl font-semibold text-sm mt-4"
                style={{
                  background: busy ? colors.disabledBg : gradients.gold,
                  color: busy ? colors.disabledText : colors.navy,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Finishing…' : "I've saved these — continue"}
              </button>
            </div>
          )}

          {phase !== 'recovery' && (
            <button
              type="button"
              onClick={signOut}
              disabled={busy}
              className="w-full py-2 text-sm mt-3"
              style={{ background: 'transparent', color: colors.muted, cursor: busy ? 'not-allowed' : 'pointer' }}
            >
              Sign out and return to login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Pull the base32 secret out of an `otpauth://` URI for manual entry. */
function extractSecret(uri: string): string {
  try {
    const params = new URL(uri).searchParams;
    return params.get('secret') ?? uri;
  } catch {
    return uri;
  }
}
